import { type UseFormReturn, useForm } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { EditClientAdminUserFormFieldValues, editClientAdminUserSchema } from '../logics/schema';
import { useEditClientAdminUser } from './useEditClientAdminUser';

type Args = {
  userId: number;
  name: string;
  email: string;
};

type IUseEditClientAdminUserModal = (args: Args) => {
  state: {
    methods: UseFormReturn<EditClientAdminUserFormFieldValues>;
  };
  actions: {
    handleSubmit: () => void;
  };
};

export const useEditClientAdminUserModal: IUseEditClientAdminUserModal = ({
  userId,
  name,
  email,
}) => {
  const { showToast } = useToast();
  const methods = useForm<EditClientAdminUserFormFieldValues>({
    defaultValues: {
      fullName: name,
      email: email,
      password: '',
      passwordConfirmation: '',
    },
    criteriaMode: 'all',
    mode: 'onChange',
    resolver: zodResolver(editClientAdminUserSchema),
  });

  const threeLetterCode = getThreeLetterCode() || '';

  const { trigger } = useEditClientAdminUser(threeLetterCode, userId, {
    swr: {
      onSuccess: () => {
        showToast({
          message: '管理者情報を更新しました。',
          status: 'success',
        });
      },
      onError: () => {
        showToast({
          message: '管理者情報の更新に失敗しました。',
          status: 'danger',
        });
      },
    },
  });

  const handleSubmit = methods.handleSubmit(async data => {
    await trigger({
      client_admin_user: {
        full_name: data.fullName,
        email: data.email,
        password: data.password || undefined,
        password_confirmation: data.passwordConfirmation || undefined,
      },
    });
  });

  return {
    state: {
      methods,
    },
    actions: {
      handleSubmit,
    },
  };
};
